import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useSearch, useRecentSearches, SearchResult } from "@/hooks/useSearch";
import { FileText, FolderOpen, Wrench, Clock, Loader2 } from "lucide-react";
import { useDebouncedValue } from "@/hooks/use-debounce";

// Strip HTML tags from text for display
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results = [], isLoading } = useSearch(debouncedQuery);
  const { recentSearches, addRecentSearch, clearRecentSearches } = useRecentSearches();

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = useCallback((result: SearchResult) => {
    const path = result.content_type === 'blog'
      ? `/blog/${result.slug}`
      : result.content_type === 'project'
      ? `/project/${result.slug}`
      : `/webtools/${result.slug}`;

    addRecentSearch(query);
    navigate(path);
    setOpen(false);
    setQuery("");
  }, [query, navigate, addRecentSearch]);

  const handleRecentSearch = useCallback((recentQuery: string) => {
    setQuery(recentQuery);
  }, []);

  const handleViewAllResults = useCallback(() => {
    if (query.trim()) {
      addRecentSearch(query);
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setOpen(false);
      setQuery("");
    }
  }, [query, navigate, addRecentSearch]);

  // Group results by content type
  const blogResults = results.filter(r => r.content_type === 'blog');
  const projectResults = results.filter(r => r.content_type === 'project');
  const webtoolResults = results.filter(r => r.content_type === 'webtool');

  const hasResults = results.length > 0;
  const showRecent = query.length < 2 && recentSearches.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search blog posts, projects, and webtools..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && debouncedQuery.length >= 2 && (
          <CommandEmpty>
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Searching...</span>
            </div>
          </CommandEmpty>
        )}

        {!isLoading && debouncedQuery.length >= 2 && !hasResults && (
          <CommandEmpty>
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground">No results found for "{debouncedQuery}"</p>
              <p className="text-xs text-muted-foreground mt-2">Try different keywords or check your spelling</p>
            </div>
          </CommandEmpty>
        )}

        {debouncedQuery.length < 2 && !showRecent && (
          <CommandEmpty>
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search
            </div>
          </CommandEmpty>
        )}

        {/* Recent Searches */}
        {showRecent && (
          <>
            <CommandGroup heading="Recent Searches">
              {recentSearches.map((recentQuery, index) => (
                <CommandItem
                  key={index}
                  value={recentQuery}
                  onSelect={() => handleRecentSearch(recentQuery)}
                >
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{recentQuery}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {recentSearches.length > 0 && (
              <div className="px-2 py-1">
                <button
                  onClick={clearRecentSearches}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear recent searches
                </button>
              </div>
            )}
            <CommandSeparator />
          </>
        )}

        {/* Blog Posts */}
        {blogResults.length > 0 && (
          <>
            <CommandGroup heading="Blog Posts">
              {blogResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                >
                  <FileText className="mr-2 h-4 w-4 text-type-blog" />
                  <div className="flex flex-col">
                    <span>{result.title}</span>
                    {result.excerpt && (
                      <span className="text-xs text-muted-foreground truncate">
                        {stripHtml(result.excerpt).substring(0, 80)}...
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Projects */}
        {projectResults.length > 0 && (
          <>
            <CommandGroup heading="Projects">
              {projectResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                >
                  <FolderOpen className="mr-2 h-4 w-4 text-type-project" />
                  <div className="flex flex-col">
                    <span>{result.title}</span>
                    {result.excerpt && (
                      <span className="text-xs text-muted-foreground truncate">
                        {stripHtml(result.excerpt).substring(0, 80)}...
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Webtools */}
        {webtoolResults.length > 0 && (
          <>
            <CommandGroup heading="Webtools">
              {webtoolResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.title}
                  onSelect={() => handleSelect(result)}
                >
                  <Wrench className="mr-2 h-4 w-4 text-type-webtool" />
                  <div className="flex flex-col">
                    <span>{result.title}</span>
                    {result.excerpt && (
                      <span className="text-xs text-muted-foreground truncate">
                        {stripHtml(result.excerpt).substring(0, 80)}...
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* View All Results link */}
        {hasResults && results.length >= 5 && (
          <div className="px-2 py-2 border-t">
            <button
              onClick={handleViewAllResults}
              className="w-full text-sm text-center text-primary hover:underline"
            >
              View all {results.length} results →
            </button>
          </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}
